import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { type HTMLAttributes, type ReactNode, forwardRef, useState } from 'react';

import { cn } from '../utils/cn';

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Alert variant
   * @default 'info'
   */
  variant?: 'success' | 'error' | 'warning' | 'info';

  /**
   * Alert title
   */
  title?: string;

  /**
   * Whether the alert is dismissible
   * @default false
   */
  dismissible?: boolean;

  /**
   * Callback when alert is dismissed
   */
  onDismiss?: () => void;

  /**
   * Custom icon
   */
  icon?: ReactNode;

  /**
   * Whether to show the default icon
   * @default true
   */
  showIcon?: boolean;
}

/**
 * Alert component
 *
 * Display important messages to users with different severity levels.
 * Supports dismissible alerts and custom icons.
 *
 * @example
 * ```tsx
 * <Alert variant="success" title="Success!">
 *   Your changes have been saved.
 * </Alert>
 *
 * <Alert variant="error" dismissible onDismiss={() => console.log('dismissed')}>
 *   An error occurred while processing your request.
 * </Alert>
 * ```
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      variant = 'info',
      title,
      dismissible = false,
      onDismiss,
      icon,
      showIcon = true,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(true);

    const handleDismiss = () => {
      setIsVisible(false);
      onDismiss?.();
    };

    if (!isVisible) {
      return null;
    }

    const variantStyles = {
      success: {
        container:
          'bg-success-50 dark:bg-success-950 border-success-200 dark:border-success-800 text-success-800 dark:text-success-200',
        icon: 'text-success-400 dark:text-success-500',
        title: 'text-success-800 dark:text-success-200',
        button:
          'text-success-500 hover:text-success-600 dark:text-success-400 dark:hover:text-success-300 focus:ring-success-500',
      },
      error: {
        container:
          'bg-error-50 dark:bg-error-950 border-error-200 dark:border-error-800 text-error-800 dark:text-error-200',
        icon: 'text-error-400 dark:text-error-500',
        title: 'text-error-800 dark:text-error-200',
        button:
          'text-error-500 hover:text-error-600 dark:text-error-400 dark:hover:text-error-300 focus:ring-error-500',
      },
      warning: {
        container:
          'bg-warning-50 dark:bg-warning-950 border-warning-200 dark:border-warning-800 text-warning-800 dark:text-warning-200',
        icon: 'text-warning-400 dark:text-warning-500',
        title: 'text-warning-800 dark:text-warning-200',
        button:
          'text-warning-500 hover:text-warning-600 dark:text-warning-400 dark:hover:text-warning-300 focus:ring-warning-500',
      },
      info: {
        container:
          'bg-info-50 dark:bg-info-950 border-info-200 dark:border-info-800 text-info-800 dark:text-info-200',
        icon: 'text-info-400 dark:text-info-500',
        title: 'text-info-800 dark:text-info-200',
        button:
          'text-info-500 hover:text-info-600 dark:text-info-400 dark:hover:text-info-300 focus:ring-info-500',
      },
    };

    const defaultIcons = {
      success: CheckCircleIcon,
      error: XCircleIcon,
      warning: ExclamationTriangleIcon,
      info: InformationCircleIcon,
    };

    const IconComponent = defaultIcons[variant];
    const styles = variantStyles[variant];

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-lg border p-4',
          'transition-all duration-200',
          styles.container,
          className
        )}
        role="alert"
        {...props}
      >
        <div className="flex">
          {showIcon && (
            <div className="flex-shrink-0">
              {icon || <IconComponent className={cn('h-5 w-5', styles.icon)} aria-hidden="true" />}
            </div>
          )}
          <div className={cn('flex-1', showIcon && 'ml-3')}>
            {title && <h3 className={cn('text-sm font-semibold mb-1', styles.title)}>{title}</h3>}
            <div className="text-sm">{children}</div>
          </div>
          {dismissible && (
            <div className="ml-auto pl-3">
              <button
                type="button"
                onClick={handleDismiss}
                className={cn(
                  'inline-flex rounded-md p-1.5',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2',
                  styles.button
                )}
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
);

Alert.displayName = 'Alert';
